import { useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, Loader2, RotateCcw } from "lucide-react";
import { API } from "../../../App";

export const DiRescorePanel = ({ headers, onCompleted }) => {
  const [job, setJob] = useState(null);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const poll = async () => {
    try {
      const r = await axios.get(`${API}/deals/rescore-jobs/latest`, { headers });
      setMeta(r.data); setJob(r.data.job); setError("");
      return r.data.job;
    } catch (e) { setError(e?.response?.data?.detail || e.message); return null; }
  };
  useEffect(() => { poll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (job?.status !== "running") return undefined;
    const t = setInterval(async () => { const j = await poll(); if (j && j.status !== "running") { clearInterval(t); onCompleted?.(); } }, 1500);
    return () => clearInterval(t);
  }, [job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/deals/score-all`, {}, { headers }); setJob(r.data.job); }
    catch (e) { setError(e?.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  const retry = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/deals/rescore-jobs/${job.id}/retry-failed`, {}, { headers }); setJob(r.data.job); }
    catch (e) { setError(e?.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  const running = job?.status === "running";
  const pctDone = job?.total ? Math.round((job.processed / job.total) * 100) : 0;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-4" data-testid="di-rescore-panel">
      <div className="flex-1 min-w-[220px]">
        <div className="flex items-center justify-between text-sm">
          <span className="font-bold text-[#0A2342]">Re-Score All</span>
          <span className="text-xs text-slate-400">Scoring v{meta?.scoring_version || "—"}</span>
        </div>
        {job ? (
          <>
            <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#C8A96B] transition-[width]" style={{ width: `${pctDone}%` }} /></div>
            <dl className="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-2 text-xs text-slate-600" data-testid="di-rescore-stats">
              <div><dt className="text-slate-400">Total</dt><dd className="font-semibold">{job.total}</dd></div>
              <div><dt className="text-slate-400">Processed</dt><dd className="font-semibold">{job.processed}</dd></div>
              <div><dt className="text-slate-400">Remaining</dt><dd className="font-semibold">{job.remaining}</dd></div>
              <div><dt className="text-slate-400">Failed</dt><dd className={`font-semibold ${job.failed ? "text-red-600" : ""}`}>{job.failed}</dd></div>
              <div><dt className="text-slate-400">Status</dt><dd className="font-semibold capitalize" data-testid="di-rescore-status">{job.status}</dd></div>
              <div><dt className="text-slate-400">Last run</dt><dd className="font-semibold">{new Date(job.completed_at || job.started_at).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" })}</dd></div>
            </dl>
          </>
        ) : <p className="text-xs text-slate-500 mt-1">No re-score job has run yet.</p>}
        {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={start} disabled={busy || running} data-testid="di-rescore-all-btn"
          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-[#0A2342] text-white text-sm font-semibold disabled:opacity-50">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} {running ? "Running…" : "Re-Score All"}
        </button>
        {job?.failed > 0 && job.status === "completed" && (
          <button type="button" onClick={retry} disabled={busy} data-testid="di-rescore-retry-btn"
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-slate-300 text-sm"><RotateCcw className="w-4 h-4" /> Retry failed</button>
        )}
      </div>
    </section>
  );
};
